import React, {useState, useRef, useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../Auth/AuthContext';
import {listProfiles as listProfilesApi, createProfile as createProfileApi, updateProfile as updateProfileApi, submitForReview as submitForReviewApi} from '../../utils/designerApi';
import {BlockNoteSchema, defaultBlockSpecs} from '@blocknote/core';
import {SideMenuExtension} from '@blocknote/core/extensions';
import {useCreateBlockNote, SuggestionMenuController, getDefaultReactSlashMenuItems, SideMenuController, SideMenu, DragHandleMenu, RemoveBlockItem, BlockColorsItem, useComponentsContext, useBlockNoteEditor, useExtensionState} from '@blocknote/react';
import {BlockNoteView} from '@blocknote/mantine';

// Block-specific options configuration - matches propSchema in CustomBlocks.jsx
const BLOCK_OPTIONS_CONFIG = {
    askAI: {
        options: [
            {
                label: 'Alignment',
                prop: 'alignment',
                values: [
                    {value: 'left', label: 'Left'},
                    {value: 'center', label: 'Center'},
                    {value: 'right', label: 'Right'}
                ]
            }
        ]
    },
    gallery: {
        options: [
            {
                label: 'Columns',
                prop: 'columns',
                values: [
                    {value: '2', label: '2 Columns'},
                    {value: '3', label: '3 Columns'},
                    {value: '4', label: '4 Columns'}
                ]
            },
            {
                label: 'Description',
                prop: 'showDescription',
                values: [
                    {value: 'true', label: 'Show'},
                    {value: 'false', label: 'Hide'}
                ]
            }
        ],
        actions: [
            {label: 'Browse Products from Brands', actionType: 'browseProducts'}
        ]
    },
    productGallery: {
        options: [
            {
                label: 'Button Style',
                prop: 'buttonStyle',
                values: [
                    {value: 'filled', label: 'Filled'},
                    {value: 'outline', label: 'Outline'}
                ]
            },
            {
                label: 'Layout',
                prop: 'layout',
                values: [
                    {value: 'row', label: 'Row'},
                    {value: 'grid', label: 'Grid'}
                ]
            }
        ],
        actions: [
            {label: 'Browse Products from Brands', actionType: 'browseProducts'}
        ]
    },
    productCard: {
        options: [
            {
                label: 'Layout',
                prop: 'layout',
                values: [
                    {value: 'vertical', label: 'Vertical'},
                    {value: 'horizontal', label: 'Horizontal'}
                ]
            },
            {
                label: 'Size',
                prop: 'size',
                values: [
                    {value: 'full', label: 'Full Width'},
                    {value: 'small', label: 'Half Width'}
                ]
            }
        ],
        actions: [
            {label: 'Browse from Brands', actionType: 'browseProducts'}
        ]
    },
    linkButton: {
        options: [
            {
                label: 'Style',
                prop: 'style',
                values: [
                    {value: 'filled', label: 'Filled'},
                    {value: 'outline', label: 'Outline'}
                ]
            },
            {
                label: 'Size',
                prop: 'size',
                values: [
                    {value: 'full', label: 'Full Width'},
                    {value: 'small', label: 'Half Width'}
                ]
            }
        ]
    },
    shoppablePost: {
        options: [
            {
                label: 'Size',
                prop: 'size',
                values: [
                    {value: 'full', label: 'Full Width'},
                    {value: 'small', label: 'Half Width'}
                ]
            }
        ]
    },
    socialLinks: {
        options: [
            {
                label: 'Icon Style',
                prop: 'iconStyle',
                values: [
                    {value: 'filled', label: 'Filled'},
                    {value: 'outline', label: 'Outline'}
                ]
            },
            {
                label: 'Icon Size',
                prop: 'iconSize',
                values: [
                    {value: 'small', label: 'Small'},
                    {value: 'medium', label: 'Medium'},
                    {value: 'large', label: 'Large'}
                ]
            },
            {
                label: 'Alignment',
                prop: 'align',
                values: [
                    {value: 'left', label: 'Left'},
                    {value: 'center', label: 'Center'},
                    {value: 'right', label: 'Right'}
                ]
            }
        ]
    },
    profileHeader: {
        options: [
            {
                label: 'Layout',
                prop: 'layout',
                values: [
                    {value: 'centered', label: 'Centered'},
                    {value: 'compact', label: 'Compact'}
                ]
            }
        ]
    },
    shoppableVideo: {
        options: [
            {
                label: 'Size',
                prop: 'size',
                values: [
                    {value: 'full', label: 'Full Width'},
                    {value: 'small', label: 'Half Width'}
                ]
            }
        ]
    },
    sectionTitle: {
        options: [
            {
                label: 'Layout',
                prop: 'layout',
                values: [
                    {value: 'column', label: 'Column'},
                    {value: 'row', label: 'Row'}
                ]
            },
            {
                label: 'Alignment',
                prop: 'align',
                values: [
                    {value: 'left', label: 'Left'},
                    {value: 'center', label: 'Center'},
                    {value: 'right', label: 'Right'}
                ]
            }
        ]
    }
};

// Custom menu items component for DragHandleMenu
const CustomDragHandleMenuItems = ({editor: propEditor}) => {
    const [activeSubmenu, setActiveSubmenu] = React.useState(null);

    // Use the hook to get the editor from context (more reliable)
    const contextEditor = useBlockNoteEditor();
    const editor = propEditor || contextEditor;

    // Use the SideMenuExtension state to get the currently hovered block
    const sideMenuState = useExtensionState(SideMenuExtension, {
        selector: (state) => state
    });

    const block = sideMenuState?.block;
    const blockType = block?.type;
    const config = BLOCK_OPTIONS_CONFIG[blockType];

    console.log('CustomDragHandleMenuItems - sideMenuState:', sideMenuState, 'blockType:', blockType, 'Config:', config);

    const hasCustomOptions = config && (config.options?.length > 0 || config.actions?.length > 0);

    const handleOptionChange = (prop, value) => {
        // Get the fresh block data from editor to avoid stale reference
        const currentBlock = block?.id ? editor.getBlock(block.id) : block;
        console.log('handleOptionChange - before update:', {
            prop,
            value,
            staleProps: block?.props,
            freshProps: currentBlock?.props,
            blockId: block?.id
        });
        if (currentBlock) {
            editor.updateBlock(currentBlock, {
                props: {...currentBlock.props, [prop]: value}
            });
        }
        console.log('handleOptionChange - after update');
        setActiveSubmenu(null);
    };

    const handleAction = (actionType) => {
        // Get the fresh block data from editor to avoid stale reference
        const currentBlock = block?.id ? editor.getBlock(block.id) : block;
        console.log('handleAction called:', actionType, 'block:', currentBlock?.id, currentBlock?.type);
        // Trigger action by setting a special prop on the block
        // The block component will watch for this and trigger its action
        if (actionType === 'browseProducts' && currentBlock) {
            const timestamp = Date.now();
            console.log('Setting _triggerBrowse:', timestamp);
            editor.updateBlock(currentBlock, {
                props: {...currentBlock.props, _triggerBrowse: timestamp}
            });
        }
        // Also dispatch event for any other listeners
        const event = new CustomEvent('blockAction', {
            detail: {blockId: currentBlock?.id, actionType}
        });
        window.dispatchEvent(event);
    };

    const menuItemStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        cursor: 'pointer',
        fontSize: '14px',
        width: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        textAlign: 'left',
        color: '#333',
        borderRadius: '4px'
    };

    const submenuStyle = {
        position: 'absolute',
        left: 'calc(100% - 8px)',
        top: '-4px',
        paddingLeft: '12px',
        zIndex: 1001
    };

    const submenuInnerStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '4px',
        minWidth: '140px',
        border: '1px solid #e5e5e5'
    };

    return (
        <>
            {/* Block-specific options (only for custom blocks) */}
            {hasCustomOptions && (
                <>
                    {config?.options?.map((option, idx) => (
                        <div
                            key={idx}
                            style={{position: 'relative'}}
                            onMouseEnter={() => setActiveSubmenu(option.prop)}
                            onMouseLeave={() => setActiveSubmenu(null)}
                        >
                            <button
                                style={menuItemStyle}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="3"/>
                                    <path d="M12 1v6m0 10v6m-9-9h6m10 0h6"/>
                                </svg>
                                <span style={{flex: 1}}>{option.label}</span>
                                <span style={{color: '#999', fontSize: '12px'}}>
                                    {option.values.find(v => v.value === block?.props?.[option.prop])?.label || option.values[0]?.label}
                                </span>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                                    <path d="M9 18l6-6-6-6"/>
                                </svg>
                            </button>
                            {activeSubmenu === option.prop && (
                                <div style={submenuStyle}>
                                    <div style={submenuInnerStyle}>
                                        {option.values.map((val, vidx) => (
                                            <button
                                                key={vidx}
                                                style={{
                                                    ...menuItemStyle,
                                                    backgroundColor: block?.props?.[option.prop] === val.value ? '#f0f0f0' : 'transparent'
                                                }}
                                                onClick={() => handleOptionChange(option.prop, val.value)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = block?.props?.[option.prop] === val.value ? '#f0f0f0' : 'transparent'}
                                            >
                                                {val.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Block-specific actions */}
                    {config?.actions?.map((action, idx) => (
                        <button
                            key={`action-${idx}`}
                            style={menuItemStyle}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => handleAction(action.actionType)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="M21 21l-4.35-4.35"/>
                            </svg>
                            <span>{action.label}</span>
                        </button>
                    ))}

                    {/* Divider after custom options */}
                    <div style={{height: '1px', backgroundColor: '#eee', margin: '4px 0'}} />
                </>
            )}

            {/* Default BlockNote menu items - styled via CSS */}
            <RemoveBlockItem>Delete</RemoveBlockItem>
            <BlockColorsItem>Colors</BlockColorsItem>
        </>
    );
};

// Fully custom side menu wrapper that bypasses BlockNote's DragHandleMenu (legacy, kept for reference)
const CustomSideMenuWrapper = ({editor, block, addBlock}) => {
    const [showMenu, setShowMenu] = React.useState(false);
    const [showSubmenu, setShowSubmenu] = React.useState(null);
    const menuRef = React.useRef(null);
    const blockType = block?.type;
    const config = BLOCK_OPTIONS_CONFIG[blockType];

    // Debug: log block type to console
    console.log('CustomSideMenuWrapper - Block type:', blockType, 'Has config:', !!config, 'Options:', config?.options?.length || 0, 'Actions:', config?.actions?.length || 0);

    // Show custom options if config exists for this block type
    const hasCustomOptions = config && (config.options?.length > 0 || config.actions?.length > 0);

    // Debug: log when menu opens
    React.useEffect(() => {
        if (showMenu) {
            console.log('Menu opened for block:', blockType, 'hasCustomOptions:', hasCustomOptions, 'config:', config);
        }
    }, [showMenu, blockType, hasCustomOptions, config]);

    // Close menu when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
                setShowSubmenu(null);
            }
        };
        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMenu]);

    const handleOptionChange = (prop, value) => {
        editor.updateBlock(block, {
            props: {...block.props, [prop]: value}
        });
        setShowSubmenu(null);
    };

    const handleAction = (actionType) => {
        // Trigger action by setting a special prop on the block
        if (actionType === 'browseProducts') {
            editor.updateBlock(block, {
                props: {...block.props, _triggerBrowse: Date.now()}
            });
        }
        const event = new CustomEvent('blockAction', {
            detail: {blockId: block.id, actionType}
        });
        window.dispatchEvent(event);
    };

    const handleDuplicate = () => {
        const blockContent = editor.getBlock(block.id);
        if (blockContent) {
            editor.insertBlocks([{...blockContent, id: undefined}], block, 'after');
        }
    };

    const handleDelete = () => {
        editor.removeBlocks([block]);
    };

    const menuItemStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        cursor: 'pointer',
        borderRadius: '4px',
        fontSize: '14px',
        width: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        textAlign: 'left',
        color: '#333'
    };

    const submenuStyle = {
        position: 'absolute',
        left: 'calc(100% - 8px)',
        top: '-4px',
        paddingLeft: '12px',
        zIndex: 1001
    };

    const submenuInnerStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '4px',
        minWidth: '140px',
        border: '1px solid #e5e5e5'
    };

    const sideMenuStyles = {
        container: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
        },
        button: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            borderRadius: '6px',
            color: '#9b9b9b'
        },
        menuWrapper: {
            position: 'relative'
        },
        menu: {
            position: 'absolute',
            top: '100%',
            left: '0',
            marginTop: '4px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '4px',
            minWidth: '220px',
            zIndex: 9999,
            border: '1px solid #e5e5e5'
        }
    };

    return (
        <div style={sideMenuStyles.container} ref={menuRef}>
            {/* Add block button */}
            <button
                style={sideMenuStyles.button}
                onClick={() => addBlock()}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; e.currentTarget.style.color = '#666'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#9b9b9b'; }}
                title="Add block"
            >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
            </button>

            {/* Drag handle with dropdown menu */}
            <div style={sideMenuStyles.menuWrapper}>
                <button
                    style={sideMenuStyles.button}
                    onClick={() => setShowMenu(!showMenu)}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f0f0'; e.currentTarget.style.color = '#666'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#9b9b9b'; }}
                    title="Drag handle / Menu"
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="5" r="2.5"/>
                        <circle cx="15" cy="5" r="2.5"/>
                        <circle cx="9" cy="12" r="2.5"/>
                        <circle cx="15" cy="12" r="2.5"/>
                        <circle cx="9" cy="19" r="2.5"/>
                        <circle cx="15" cy="19" r="2.5"/>
                    </svg>
                </button>

                {/* Dropdown menu */}
                {showMenu && (
                    <div style={sideMenuStyles.menu}>
                        {/* Block-specific options */}
                        {hasCustomOptions && config?.options?.map((option, idx) => (
                            <div
                                key={idx}
                                style={{position: 'relative'}}
                                onMouseEnter={() => setShowSubmenu(option.prop)}
                                onMouseLeave={() => setShowSubmenu(null)}
                            >
                                <button
                                    style={menuItemStyle}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="3"/>
                                        <path d="M12 1v6m0 10v6m-9-9h6m10 0h6"/>
                                    </svg>
                                    <span style={{flex: 1}}>{option.label}</span>
                                    <span style={{color: '#999', fontSize: '12px'}}>
                                        {option.values.find(v => v.value === block.props?.[option.prop])?.label || option.values[0]?.label}
                                    </span>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
                                        <path d="M9 18l6-6-6-6"/>
                                    </svg>
                                </button>
                                {showSubmenu === option.prop && (
                                    <div style={submenuStyle}>
                                        {option.values.map((val, vidx) => (
                                            <button
                                                key={vidx}
                                                style={{
                                                    ...menuItemStyle,
                                                    backgroundColor: block.props?.[option.prop] === val.value ? '#f0f0f0' : 'transparent'
                                                }}
                                                onClick={() => { handleOptionChange(option.prop, val.value); setShowMenu(false); }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = block.props?.[option.prop] === val.value ? '#f0f0f0' : 'transparent'}
                                            >
                                                {val.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Block-specific actions */}
                        {hasCustomOptions && config?.actions?.map((action, idx) => (
                            <button
                                key={`action-${idx}`}
                                style={menuItemStyle}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                onClick={() => { handleAction(action.actionType); setShowMenu(false); }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"/>
                                    <path d="M21 21l-4.35-4.35"/>
                                </svg>
                                <span>{action.label}</span>
                            </button>
                        ))}

                        {/* Divider */}
                        {hasCustomOptions && (
                            <div style={{height: '1px', backgroundColor: '#eee', margin: '4px 0'}} />
                        )}

                        {/* Duplicate */}
                        <button
                            style={menuItemStyle}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => { handleDuplicate(); setShowMenu(false); }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2"/>
                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                            </svg>
                            <span style={{flex: 1}}>Duplicate</span>
                            <span style={{fontSize: '11px', color: '#999'}}>Ctrl+D</span>
                        </button>

                        {/* Delete */}
                        <button
                            style={{...menuItemStyle, color: '#dc2626'}}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            onClick={() => { handleDelete(); setShowMenu(false); }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                            </svg>
                            <span>Delete</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
import '@blocknote/mantine/style.css';
import '@blocknote/core/fonts/inter.css';

// Simple filter function for slash menu items
const filterSlashMenuItems = (items, query) => {
    if (!query) {return items;}
    const lowerQuery = query.toLowerCase();
    return items.filter(item => {
        const titleMatch = item.title.toLowerCase().includes(lowerQuery);
        const aliasMatch = item.aliases?.some(alias => alias.toLowerCase().includes(lowerQuery));
        return titleMatch || aliasMatch;
    });
};

// Helper function to insert a block
const insertBlock = (editor, blockType) => {
    const currentBlock = editor.getTextCursorPosition().block;
    editor.insertBlocks([{type: blockType}], currentBlock, 'after');
};

// Custom blocks
import {
    AskAIBlock,
    ShoppablePostBlock,
    ProductCardBlock,
    LinkButtonBlock,
    SocialLinksBlock,
    ProfileHeaderBlock,
    ShoppableVideoBlock,
    SectionTitleBlock,
    GalleryBlock,
    ProductGalleryBlock,
    CartProvider,
    FloatingCheckoutBar
} from './CustomBlocks';

// Create custom schema with our blocks
// createReactBlockSpec returns a function that must be called
const customSchema = BlockNoteSchema.create({
    blockSpecs: {
        ...defaultBlockSpecs,
        askAI: AskAIBlock(),
        shoppablePost: ShoppablePostBlock(),
        productCard: ProductCardBlock(),
        linkButton: LinkButtonBlock(),
        socialLinks: SocialLinksBlock(),
        profileHeader: ProfileHeaderBlock(),
        shoppableVideo: ShoppableVideoBlock(),
        sectionTitle: SectionTitleBlock(),
        gallery: GalleryBlock(),
        productGallery: ProductGalleryBlock()
    }
});

// Social Platforms Configuration
const SOCIAL_PLATFORMS = [
    {id: 'tiktok', name: 'TikTok', icon: 'M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z'},
    {id: 'youtube', name: 'YouTube', icon: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'},
    {id: 'twitter', name: 'X', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'},
    {id: 'instagram', name: 'Instagram', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'},
    {id: 'linkedin', name: 'LinkedIn', icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'},
    {id: 'github', name: 'GitHub', icon: 'M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z'}
];

// Generate unique ID
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

// Custom slash menu items for our blocks
const getCustomSlashMenuItems = (editor) => [
    {
        key: 'profile_header',
        title: 'Profile Header',
        onItemClick: () => {
            insertBlock(editor, 'profileHeader');
        },
        aliases: ['profile', 'header', 'avatar', 'bio', 'profileheader'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg>,
        subtext: 'Add a profile header with avatar and bio'
    },
    {
        key: 'product_gallery',
        title: 'Product Gallery',
        onItemClick: () => {
            insertBlock(editor, 'productGallery');
        },
        aliases: ['products', 'gallery', 'shop', 'store', 'ecommerce', 'productgallery'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
        subtext: 'Add a product gallery grid'
    },
    {
        key: 'product_card',
        title: 'Product Card',
        onItemClick: () => {
            insertBlock(editor, 'productCard');
        },
        aliases: ['product', 'card', 'item', 'merchandise', 'productcard'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/></svg>,
        subtext: 'Add a single product card'
    },
    {
        key: 'link_button',
        title: 'Link Button',
        onItemClick: () => {
            insertBlock(editor, 'linkButton');
        },
        aliases: ['button', 'link', 'cta', 'action', 'linkbutton'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="8" width="18" height="8" rx="4"/></svg>,
        subtext: 'Add a call-to-action button'
    },
    {
        key: 'social_links',
        title: 'Social Links',
        onItemClick: () => {
            insertBlock(editor, 'socialLinks');
        },
        aliases: ['social', 'links', 'instagram', 'twitter', 'tiktok', 'youtube', 'sociallinks'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
        subtext: 'Add social media links'
    },
    {
        key: 'section_title',
        title: 'Section Title',
        onItemClick: () => {
            insertBlock(editor, 'sectionTitle');
        },
        aliases: ['section', 'title', 'divider', 'sectiontitle'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"/><polyline points="8 8 12 4 16 8"/></svg>,
        subtext: 'Add a section title/divider'
    },
    {
        key: 'gallery_block',
        title: 'Image Gallery',
        onItemClick: () => {
            insertBlock(editor, 'gallery');
        },
        aliases: ['images', 'photos', 'grid', 'portfolio', 'imagegallery'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
        subtext: 'Add an image gallery'
    },
    {
        key: 'shoppable_post',
        title: 'Shoppable Post',
        onItemClick: () => {
            insertBlock(editor, 'shoppablePost');
        },
        aliases: ['shoppable', 'post', 'tagged', 'shoppablepost'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M16 8a4 4 0 01-8 0"/></svg>,
        subtext: 'Add a shoppable social post'
    },
    {
        key: 'shoppable_video',
        title: 'Shoppable Video',
        onItemClick: () => {
            insertBlock(editor, 'shoppableVideo');
        },
        aliases: ['video', 'reel', 'shoppablevideo'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
        subtext: 'Add a shoppable video'
    },
    {
        key: 'ask_ai',
        title: 'Ask AI',
        onItemClick: () => {
            insertBlock(editor, 'askAI');
        },
        aliases: ['ai', 'assistant', 'help', 'generate', 'askai'],
        group: 'Custom Blocks',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 011 1v3a1 1 0 01-1 1h-1v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1H2a1 1 0 01-1-1v-3a1 1 0 011-1h1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2z"/><circle cx="8" cy="14" r="1"/><circle cx="16" cy="14" r="1"/></svg>,
        subtext: 'Add an AI assistant block'
    }
];

// Profile Templates data
const PROFILE_TEMPLATES = [
    {
        id: 'creator-shop',
        name: 'Creator Shop',
        subtitle: 'E-commerce / Influencer',
        background: 'linear-gradient(180deg, #f5f5f5 0%, #e8e8e8 100%)',
        themeColor: '#f5f5f5',
        preview: {
            avatar: true,
            avatarCompact: true,
            avatarImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
            title: 'Leo Rossi',
            tagline: '',
            socialIcons: ['instagram', 'tiktok'],
            sectionTitle: 'Top Picks',
            productCards: [
                {name: 'Travel Backpack', price: '$120', image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=100&h=100&fit=crop'},
                {name: 'Portable Charger', price: '$45', image: 'https://images.unsplash.com/photo-1609091839311-d5365f9ff1c5?w=100&h=100&fit=crop'}
            ],
            videoSection: true,
            videoThumbnail: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=200&h=120&fit=crop'
        }
    },
    {
        id: 'coach-jax',
        name: 'Coach Jax',
        subtitle: 'Fitness / Coaching',
        background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
        themeColor: '#f8e8e8',
        preview: {
            avatar: true,
            avatarCompact: true,
            avatarImage: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=100&h=100&fit=crop',
            title: 'Coach Jax',
            tagline: 'Transform your body',
            socialIcons: ['instagram', 'youtube'],
            sectionTitle: 'Training Programs',
            productCards: [
                {name: '12 Week Program', price: '$199', image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=100&h=100&fit=crop'},
                {name: 'Meal Plans', price: '$49', image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=100&h=100&fit=crop'}
            ],
            buttons: ['Book Consultation']
        }
    },
    {
        id: 'elara-designs',
        name: 'Elara Designs',
        subtitle: 'Fashion / Design',
        background: 'linear-gradient(180deg, #fef5ef 0%, #fce4d6 100%)',
        themeColor: '#f5f0e8',
        preview: {
            avatar: true,
            avatarCompact: true,
            avatarImage: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
            title: 'Elara Designs',
            tagline: 'Handcrafted jewelry',
            socialIcons: ['instagram', 'pinterest'],
            sectionTitle: 'Featured Collection',
            shoppablePost: true,
            shoppableImage: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=200&h=120&fit=crop',
            buttons: ['Shop Collection']
        }
    },
    {
        id: 'zed-gaming',
        name: 'Zed_Gaming',
        subtitle: 'Gaming / Streaming',
        background: 'linear-gradient(180deg, #0d0d0d 0%, #1a0a2e 100%)',
        themeColor: '#4a9c8c',
        preview: {
            avatar: true,
            avatarCompact: true,
            avatarImage: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=100&h=100&fit=crop',
            title: 'Zed_Gaming',
            tagline: 'Pro Gamer & Streamer',
            liveStatus: true,
            socialIcons: ['twitch', 'youtube', 'discord'],
            buttons: ['Watch Live', 'Join Discord']
        }
    },
    {
        id: 'fixers-bench',
        name: "The Fixer's Bench",
        subtitle: 'Tech / Repair',
        background: 'linear-gradient(180deg, #2d3436 0%, #1e272e 100%)',
        themeColor: '#2d3436',
        preview: {
            avatar: true,
            avatarCompact: true,
            avatarImage: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=100&h=100&fit=crop',
            title: "The Fixer's Bench",
            tagline: 'Device repair specialist',
            socialIcons: ['youtube', 'twitter'],
            videoEmbed: true,
            videoThumbnail: 'https://images.unsplash.com/photo-1597872200969-2b65d56bd16b?w=200&h=120&fit=crop',
            checklistItems: ['Phone Repair', 'Laptop Fix', 'Console Mod'],
            buttons: ['Book Repair']
        }
    },
    {
        id: 'tech-creator',
        name: 'Alex Chen',
        subtitle: 'Tech / Creator',
        background: 'linear-gradient(180deg, #f5f5f0 0%, #e8e8e0 100%)',
        themeColor: '#f5f5f5',
        preview: {
            avatar: true,
            avatarCompact: false,
            avatarImage: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
            title: 'Alex Chen',
            tagline: 'Creator & Photographer',
            askAI: true,
            shoppablePost: true,
            shoppableImage: 'https://images.unsplash.com/photo-1593062096033-9a26b09da705?w=200&h=120&fit=crop',
            productCards: [
                {name: 'Sony WH-1000XM5', price: '$348', image: 'https://images.unsplash.com/photo-1618366712010-f4ae9c647dcb?w=100&h=100&fit=crop'}
            ]
        }
    }
];

// Block Templates - Pre-configured blocks for each template
const BLOCK_TEMPLATES = {
    'creator-shop': [
        {
            type: 'profileHeader',
            props: {
                name: 'Leo Rossi',
                bio: '',
                avatarUrl: '',
                layout: 'compact',
                instagram: '',
                tiktok: ''
            }
        },
        {
            type: 'sectionTitle',
            props: {
                title: 'Top Picks',
                layout: 'row',
                align: 'left'
            }
        },
        {
            type: 'productCard',
            props: {
                productName: 'Travel Backpack',
                price: '$120',
                imageUrl: '',
                shopUrl: '',
                size: 'small'
            }
        },
        {
            type: 'productCard',
            props: {
                productName: 'Portable Charger',
                price: '$45',
                imageUrl: '',
                shopUrl: '',
                size: 'small'
            }
        },
        {
            type: 'productCard',
            props: {
                productName: 'Camera Bag',
                price: '$89',
                imageUrl: '',
                shopUrl: '',
                size: 'small'
            }
        },
        {
            type: 'sectionTitle',
            props: {
                title: 'Shoppable Video Post',
                layout: 'column',
                align: 'left'
            }
        },
        {
            type: 'shoppableVideo',
            props: {
                title: 'Shop the look in this video',
                videoUrl: '',
                thumbnailUrl: '',
                itemCount: '3',
                size: 'full'
            }
        }
    ],
    'coach-jax': [
        {
            type: 'profileHeader',
            props: {
                name: 'Coach Jax',
                bio: 'Transform your body, transform your life',
                avatarUrl: '',
                layout: 'compact',
                instagram: '',
                tiktok: ''
            }
        },
        {
            type: 'socialLinks',
            props: {
                instagram: '',
                youtube: '',
                tiktok: '',
                twitter: '',
                layout: 'row'
            }
        },
        {
            type: 'gallery',
            props: {
                title: 'Gallery View',
                columns: '2',
                items: JSON.stringify([
                    {id: '1', name: '8-Week Shred Program', price: '$99', imageUrl: ''},
                    {id: '2', name: 'HIIT at Home', price: '$49', imageUrl: ''},
                    {id: '3', name: 'Meal Prep Guide', price: '$29', imageUrl: ''},
                    {id: '4', name: '1-on-1 Coaching', price: 'Book Now', imageUrl: ''}
                ])
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Book Consultation',
                url: '',
                style: 'filled',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Free Workout',
                url: '',
                style: 'outline',
                size: 'full'
            }
        }
    ],
    'elara-designs': [
        {
            type: 'profileHeader',
            props: {
                name: 'Elara Designs',
                bio: 'Handcrafted jewelry for every occasion',
                avatarUrl: '',
                layout: 'compact',
                instagram: '',
                tiktok: ''
            }
        },
        {
            type: 'socialLinks',
            props: {
                instagram: '',
                tiktok: '',
                youtube: '',
                twitter: '',
                layout: 'row'
            }
        },
        {
            type: 'sectionTitle',
            props: {
                title: 'Featured Collection',
                layout: 'row',
                align: 'left'
            }
        },
        {
            type: 'shoppablePost',
            props: {
                imageUrl: '',
                description: 'New summer collection - tap to shop the look',
                productCount: '4'
            }
        },
        {
            type: 'productCard',
            props: {
                productName: 'Gold Hoops',
                price: '$85',
                imageUrl: '',
                shopUrl: '',
                size: 'small'
            }
        },
        {
            type: 'productCard',
            props: {
                productName: 'Pearl Necklace',
                price: '$120',
                imageUrl: '',
                shopUrl: '',
                size: 'small'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Shop Collection',
                url: '',
                style: 'filled',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Custom Orders',
                url: '',
                style: 'outline',
                size: 'full'
            }
        }
    ],
    'zed-gaming': [
        {
            type: 'profileHeader',
            props: {
                name: 'Zed_Gaming',
                bio: 'Pro Gamer & Streamer',
                avatarUrl: '',
                layout: 'compact',
                instagram: '',
                tiktok: ''
            }
        },
        {
            type: 'socialLinks',
            props: {
                instagram: '',
                tiktok: '',
                youtube: '',
                twitter: '',
                layout: 'row'
            }
        },
        {
            type: 'sectionTitle',
            props: {
                title: '🔴 LIVE NOW',
                layout: 'column',
                align: 'center'
            }
        },
        {
            type: 'shoppableVideo',
            props: {
                title: 'Watch Live Stream',
                videoUrl: '',
                thumbnailUrl: '',
                itemCount: '0',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Watch on Twitch',
                url: '',
                style: 'filled',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Join Discord',
                url: '',
                style: 'outline',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Subscribe on YouTube',
                url: '',
                style: 'outline',
                size: 'full'
            }
        }
    ],
    'fixers-bench': [
        {
            type: 'profileHeader',
            props: {
                name: "The Fixer's Bench",
                bio: 'Device repair specialist - phones, laptops, consoles',
                avatarUrl: '',
                layout: 'compact',
                instagram: '',
                tiktok: ''
            }
        },
        {
            type: 'socialLinks',
            props: {
                instagram: '',
                youtube: '',
                tiktok: '',
                twitter: '',
                layout: 'row'
            }
        },
        {
            type: 'sectionTitle',
            props: {
                title: 'Latest Repair Video',
                layout: 'column',
                align: 'left'
            }
        },
        {
            type: 'shoppableVideo',
            props: {
                title: 'iPhone Screen Replacement Tutorial',
                videoUrl: '',
                thumbnailUrl: '',
                itemCount: '0',
                size: 'full'
            }
        },
        {
            type: 'sectionTitle',
            props: {
                title: 'Services',
                layout: 'column',
                align: 'left'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Phone Repair',
                url: '',
                style: 'outline',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Laptop Fix',
                url: '',
                style: 'outline',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Console Mod',
                url: '',
                style: 'outline',
                size: 'full'
            }
        },
        {
            type: 'linkButton',
            props: {
                text: 'Book Repair',
                url: '',
                style: 'filled',
                size: 'full'
            }
        }
    ],
    'tech-creator': [
        {
            type: 'profileHeader',
            props: {
                name: 'Alex Chen',
                bio: 'Tech creator sharing my favorite gear, workspace setups, and productivity tips on Instagram and YouTube.',
                avatarUrl: '',
                layout: 'centered',
                instagram: '',
                tiktok: '',
                youtube: '',
                twitter: ''
            }
        },
        {
            type: 'askAI',
            props: {
                aiName: "Alex's AI",
                questionPlaceholder: "What's my camera setup?"
            }
        },
        {
            type: 'shoppablePost',
            props: {
                title: 'Shoppable Post',
                description: 'My 2024 WFH essentials dedicated to focus.',
                imageUrl: '',
                size: 'full'
            }
        },
        {
            type: 'productCard',
            props: {
                productName: 'Sony WH-1000XM5',
                price: '$348',
                imageUrl: '',
                shopUrl: '',
                size: 'full'
            }
        }
    ]
};

// Default profile template
const createDefaultProfile = (name = '') => ({
    id: generateId(),
    name: name || 'My Profile',
    createdAt: new Date().toISOString(),
    settings: {
        displayName: '',
        bio: '',
        profileImage: '',
        socials: {}
    },
    editorContent: null
});

// BlockNote Editor Component
function BlockNoteEditorWrapper({initialContent, onContentChange, editorRef, onInsertBlock}) {
    const editor = useCreateBlockNote({
        schema: customSchema,
        initialContent: initialContent || undefined
    });

    // Store editor reference
    useEffect(() => {
        if (editorRef) {
            editorRef.current = editor;
        }
    }, [editor, editorRef]);

    // Expose insert block function
    useEffect(() => {
        if (onInsertBlock) {
            onInsertBlock((blockType) => {
                const currentBlock = editor.getTextCursorPosition().block;
                editor.insertBlocks(
                    [{type: blockType}],
                    currentBlock,
                    'after'
                );
            });
        }
    }, [editor, onInsertBlock]);

    // Handle content changes
    const handleChange = useCallback(() => {
        if (onContentChange) {
            const content = editor.document;
            onContentChange(content);
        }
    }, [editor, onContentChange]);

    // Combine default and custom slash menu items
    const getSlashMenuItems = useCallback(async (query) => {
        const defaultItems = getDefaultReactSlashMenuItems(editor);
        const customItems = getCustomSlashMenuItems(editor);
        return filterSlashMenuItems([...customItems, ...defaultItems], query);
    }, [editor]);

    return (
        <div className="blocknote-editor-container">
            <style>{`
                .blocknote-editor-container .bn-side-menu > button,
                .blocknote-editor-container .bn-side-menu [data-drag-handle],
                .blocknote-editor-container [data-add-block-button] {
                    min-width: 26px !important;
                    min-height: 26px !important;
                }
                .blocknote-editor-container .bn-side-menu > button svg,
                .blocknote-editor-container .bn-side-menu [data-drag-handle] svg,
                .blocknote-editor-container [data-add-block-button] svg {
                    width: 20px !important;
                    height: 20px !important;
                }
                /* Change blue selection border to subtle grey */
                .blocknote-editor-container * {
                    outline: none !important;
                }
                .blocknote-editor-container .bn-block-outer,
                .blocknote-editor-container .bn-block-content,
                .blocknote-editor-container [data-node-type],
                .blocknote-editor-container [data-content-type] {
                    outline: none !important;
                }
                /* Override blue border with subtle grey border when selected */
                .blocknote-editor-container .bn-block-outer[data-is-selected="true"] > div,
                .blocknote-editor-container .bn-block-outer:focus-within > div {
                    border: 1px solid #e0e0e0 !important;
                    border-radius: 8px !important;
                    outline: none !important;
                    box-shadow: none !important;
                }
                /* Remove the default blue styling */
                .blocknote-editor-container .bn-block-outer::before,
                .blocknote-editor-container .bn-block-outer::after,
                .blocknote-editor-container .bn-block-content::before,
                .blocknote-editor-container .bn-block-content::after {
                    display: none !important;
                }
                /* Override focus styles */
                .blocknote-editor-container [class*="bn-"]:focus,
                .blocknote-editor-container [class*="bn-"]:focus-within,
                .blocknote-editor-container [class*="bn-"]:focus-visible {
                    outline: none !important;
                    box-shadow: none !important;
                }
                .blocknote-editor-container .bn-inline-content:focus,
                .blocknote-editor-container .bn-inline-content:focus-within {
                    outline: none !important;
                    box-shadow: none !important;
                }
                /* Style default BlockNote menu items to match custom options */
                .bn-drag-handle-menu [class*="mantine-Menu-item"],
                .bn-drag-handle-menu button[data-menu-item] {
                    display: flex !important;
                    align-items: center !important;
                    gap: 8px !important;
                    padding: 8px 12px !important;
                    font-size: 14px !important;
                    color: #333 !important;
                    border-radius: 4px !important;
                }
                .bn-drag-handle-menu [class*="mantine-Menu-item"]:hover {
                    background-color: #f5f5f5 !important;
                }
                /* Add icon spacing for Delete and Colors items */
                .bn-drag-handle-menu [class*="mantine-Menu-item"] svg {
                    width: 16px !important;
                    height: 16px !important;
                    margin-right: 4px !important;
                }
            `}</style>
            <BlockNoteView
                editor={editor}
                onChange={handleChange}
                theme="light"
                slashMenu={false}
                sideMenu={false}
            >
                <SuggestionMenuController
                    triggerCharacter="/"
                    getItems={getSlashMenuItems}
                />
                <SideMenuController
                    sideMenu={(props) => (
                        <SideMenu
                            {...props}
                            dragHandleMenu={(dragProps) => (
                                <DragHandleMenu {...dragProps}>
                                    <CustomDragHandleMenuItems editor={editor} />
                                </DragHandleMenu>
                            )}
                        />
                    )}
                />
            </BlockNoteView>
        </div>
    );
}

export default function BlockNoteDesigner() {
    const {user, tokens} = useAuth();
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const avatarInputRef = useRef(null);
    const contentAreaRef = useRef(null);

    const [viewMode, setViewMode] = useState('mobile');
    const [isSaving, setIsSaving] = useState(false);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showNewProfileModal, setShowNewProfileModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [newProfileName, setNewProfileName] = useState('');
    const [renameProfileId, setRenameProfileId] = useState(null);
    const [renameProfileName, setRenameProfileName] = useState('');
    const [showSettingsPanel, setShowSettingsPanel] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);
    const [templateSheetHeight, setTemplateSheetHeight] = useState(400);
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [shareCopied, setShareCopied] = useState(false);
    const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
    const [currentEditorContent, setCurrentEditorContent] = useState(null);
    const [showBlocksMenu, setShowBlocksMenu] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [sidebarOffset, setSidebarOffset] = useState(0); // Track sidebar width for toolbar centering
    const insertBlockRef = useRef(null);
    const templateSheetRef = useRef(null);
    const colorPickerRef = useRef(null);
    const isDraggingRef = useRef(false);
    const startYRef = useRef(0);
    const startHeightRef = useRef(0);

    // Load all profiles from API
    const [profiles, setProfiles] = useState([]);
    const [profilesLoaded, setProfilesLoaded] = useState(false);

    // Check if we should create a new profile
    const [isNewProfile, setIsNewProfile] = useState(() => {
        const saved = localStorage.getItem('blocknote-designer-active-profile');
        return saved === 'new';
    });

    // Current active profile ID (from localStorage, set by MyPages)
    const [activeProfileId, setActiveProfileId] = useState(() => {
        const saved = localStorage.getItem('blocknote-designer-active-profile');
        if (saved === 'new') {return null;}
        return saved ? (isNaN(Number(saved)) ? saved : Number(saved)) : null;
    });

    // Monitor sidebar state for bottom toolbar centering
    useEffect(() => {
        const updateSidebarOffset = () => {
            // Get the content area's bounding rect to calculate sidebar offset
            if (contentAreaRef.current) {
                const rect = contentAreaRef.current.getBoundingClientRect();
                // The left position of content area equals sidebar width when open
                setSidebarOffset(rect.left);
            } else {
                // Fallback: read sidebar width from localStorage
                const savedWidth = localStorage.getItem('koenig-sidebar-width');
                const sidebarWidth = savedWidth ? parseInt(savedWidth, 10) : 240;
                // Check if sidebar is likely collapsed by checking if hamburger is visible
                const hamburger = document.querySelector('[title="Open sidebar"], [title="Lock sidebar open"]');
                const isCollapsed = hamburger && window.getComputedStyle(hamburger).opacity === '1';
                setSidebarOffset(isCollapsed ? 0 : sidebarWidth);
            }
        };

        // Initial check with a small delay to ensure DOM is ready
        const initialTimeout = setTimeout(updateSidebarOffset, 100);

        // Use MutationObserver to detect style changes
        const observer = new MutationObserver(updateSidebarOffset);
        const targetNode = document.body;
        observer.observe(targetNode, {
            attributes: true,
            subtree: true,
            attributeFilter: ['style', 'class']
        });

        // Also listen for window resize
        window.addEventListener('resize', updateSidebarOffset);

        return () => {
            clearTimeout(initialTimeout);
            observer.disconnect();
            window.removeEventListener('resize', updateSidebarOffset);
        };
    }, []);

    // Load profiles from API on mount
    useEffect(() => {
        if (!tokens?.accessToken || profilesLoaded) {return;}
        (async () => {
            const result = await listProfilesApi({accessToken: tokens.accessToken});
            if (result.ok && result.data?.profiles) {
                const apiProfiles = result.data.profiles.map(p => ({
                    id: p.id,
                    name: p.name,
                    slug: p.slug,
                    createdAt: p.createdAt,
                    updatedAt: p.updatedAt,
                    settings: p.settingsJson || {},
                    editorContent: p.editorState?.document || p.editorState || null,
                    _raw: p // keep raw API data for reference
                }));
                setProfiles(apiProfiles);

                // If we have an active ID but it doesn't match any loaded profile, use first
                if (!isNewProfile && activeProfileId) {
                    const found = apiProfiles.find(p => p.id === activeProfileId || String(p.id) === String(activeProfileId));
                    if (!found && apiProfiles.length > 0) {
                        setActiveProfileId(apiProfiles[0].id);
                    } else if (found) {
                        setActiveProfileId(found.id);
                    }
                } else if (!isNewProfile && apiProfiles.length > 0) {
                    setActiveProfileId(apiProfiles[0].id);
                }
            }
            setProfilesLoaded(true);
        })();
    }, [tokens?.accessToken, profilesLoaded, isNewProfile, activeProfileId]);

    // Get current profile (null for new profiles)
    const currentProfile = isNewProfile ? null : (profiles.find(p => String(p.id) === String(activeProfileId)) || profiles[0] || null);

    // Profile settings state
    const [settings, setSettings] = useState(() => {
        if (isNewProfile) {
            return {
                displayName: '',
                bio: '',
                profileImage: '',
                socials: {},
                backgroundColor: '#ffffff'
            };
        }
        if (currentProfile?.settings) {
            return currentProfile.settings;
        }
        return {
            displayName: user?.name || '',
            bio: '',
            profileImage: '',
            socials: {}
        };
    });

    // Editor content state
    const [initialContent, setInitialContent] = useState(() => {
        if (isNewProfile) {
            return null;
        }
        if (currentProfile?.editorContent) {
            return currentProfile.editorContent;
        }
        return null;
    });

    // Update settings when profile changes or when profiles finish loading from API
    useEffect(() => {
        if (isNewProfile) {
            // Fresh editor for new profile
            setSettings({
                pageName: '',
                displayName: '',
                bio: '',
                profileImage: '',
                socials: {},
                backgroundColor: '#ffffff'
            });
            setInitialContent(null);
            setCurrentEditorContent(null);
            setEditorKey(prev => prev + 1);
        } else if (currentProfile) {
            const loadedSettings = currentProfile.settings || {};
            setSettings({
                displayName: '',
                bio: '',
                profileImage: '',
                socials: {},
                ...loadedSettings,
                pageName: loadedSettings.pageName || currentProfile.name || ''
            });
            setInitialContent(currentProfile.editorContent || null);
            setEditorKey(prev => prev + 1);
        }
    }, [activeProfileId, isNewProfile, profilesLoaded]);

    // Helper to generate slug from name
    const generateSlug = (name) => {
        return (name || 'profile').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    };

    // Update current profile settings
    const updateSettings = (field, value) => {
        setSettings(prev => ({...prev, [field]: value}));
    };

    const updateSocial = (platform, value) => {
        setSettings(prev => ({
            ...prev,
            socials: {...prev.socials, [platform]: value}
        }));
    };

    const handleAvatarUpload = (e) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updateSettings('profileImage', reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const triggerAvatarUpload = () => {
        avatarInputRef.current?.click();
    };

    // Handle editor content change
    const handleEditorContentChange = useCallback((content) => {
        setCurrentEditorContent(content);
    }, []);

    // Save current profile via API
    const handleSave = useCallback(async () => {
        setIsSaving(true);

        // Get editor content from BlockNote
        let editorContent = currentEditorContent;
        if (editorRef.current) {
            editorContent = editorRef.current.document;
        }

        const profileName = settings.pageName || settings.displayName || 'My Profile';
        const profileSlug = generateSlug(profileName);
        const settingsToSave = {...settings, displayName: profileName};

        try {
            if (isNewProfile) {
                // Create via API
                const result = await createProfileApi({
                    accessToken: tokens?.accessToken,
                    name: profileName,
                    slug: profileSlug,
                    settingsJson: settingsToSave,
                    editorState: {document: editorContent}
                });

                if (result.ok && result.data?.profile) {
                    const created = result.data.profile;
                    const newLocal = {
                        id: created.id,
                        name: created.name,
                        slug: created.slug,
                        createdAt: created.createdAt,
                        settings: created.settingsJson || settingsToSave,
                        editorContent: editorContent,
                        _raw: created
                    };
                    setProfiles(prev => [...prev, newLocal]);
                    setActiveProfileId(created.id);
                    localStorage.setItem('blocknote-designer-active-profile', String(created.id));
                    setIsNewProfile(false);
                } else {
                    console.error('Failed to create profile:', result.error);
                }
            } else {
                // Update via API
                const result = await updateProfileApi({
                    accessToken: tokens?.accessToken,
                    id: activeProfileId,
                    name: profileName,
                    slug: profileSlug,
                    settingsJson: settingsToSave,
                    editorState: {document: editorContent}
                });

                if (result.ok && result.data?.profile) {
                    const updated = result.data.profile;
                    setProfiles(prev => prev.map(p => {
                        if (String(p.id) === String(activeProfileId)) {
                            return {
                                ...p,
                                name: updated.name,
                                slug: updated.slug,
                                settings: updated.settingsJson || settingsToSave,
                                editorContent: editorContent,
                                updatedAt: updated.updatedAt,
                                _raw: updated
                            };
                        }
                        return p;
                    }));
                } else {
                    console.error('Failed to update profile:', result.error);
                }
            }
        } catch (err) {
            console.error('Save error:', err);
            console.error('An error occurred while saving');
        }

        setTimeout(() => {
            setIsSaving(false);
            setShowSaveSuccessModal(true);
        }, 500);
    }, [settings, profiles, activeProfileId, currentEditorContent, isNewProfile, tokens?.accessToken]);

    const handlePreview = useCallback(() => {
        handleSave();
        setTimeout(() => {
            navigate('/blocknote-designer/preview');
        }, 100);
    }, [navigate, handleSave]);

    // Track whether profile has unsaved changes (dirty flag resets on save)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true);
    const [isPublishing, setIsPublishing] = useState(false);

    // Reset dirty flag when save completes
    useEffect(() => {
        if (!isSaving && showSaveSuccessModal) {
            setHasUnsavedChanges(false);
        }
    }, [isSaving, showSaveSuccessModal]);

    // Mark dirty on settings or editor content change
    useEffect(() => {
        if (profilesLoaded) {
            setHasUnsavedChanges(true);
        }
    }, [settings, currentEditorContent]);

    // Submit for review / publish
    const handlePublish = useCallback(async () => {
        if (!activeProfileId) {
            return;
        }
        setIsPublishing(true);
        const result = await submitForReviewApi({accessToken: tokens?.accessToken, id: activeProfileId});
        if (result.ok) {
            // Update the local raw data to reflect the new status
            setProfiles(prev => prev.map(p => {
                if (String(p.id) === String(activeProfileId)) {
                    return {
                        ...p,
                        _raw: {...(p._raw || {}), status: 'pending_review'}
                    };
                }
                return p;
            }));
            // success — status updated locally
        } else {
            console.error('Failed to submit for review:', result.error);
        }
        setIsPublishing(false);
    }, [activeProfileId, tokens?.accessToken]);

    // Generate shareable URL (mydomain/profile-slug)
    const handleShare = useCallback(() => {
        const currentRaw = currentProfile?._raw;
        const slug = currentRaw?.slug || currentProfile?.slug;

        if (!slug) {
            return;
        }

        const url = `${window.location.origin}/${slug}`;
        setShareUrl(url);
        setShareCopied(false);
        setShowShareModal(true);
    }, [currentProfile]);

    // Copy share URL to clipboard
    const handleCopyShareUrl = useCallback(() => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        });
    }, [shareUrl]);

    // Create new profile via API
    const handleCreateProfile = useCallback(async () => {
        const profileName = newProfileName.trim();
        if (!profileName) {return;}

        setIsTransitioning(true);
        setShowNewProfileModal(false);

        // Save current profile first
        let editorContent = currentEditorContent;
        if (editorRef.current) {
            editorContent = editorRef.current.document;
        }
        if (activeProfileId && !isNewProfile) {
            await updateProfileApi({
                accessToken: tokens?.accessToken,
                id: activeProfileId,
                settingsJson: {...settings},
                editorState: {document: editorContent}
            });
        }

        // Create new profile via API
        const slug = generateSlug(profileName);
        const result = await createProfileApi({
            accessToken: tokens?.accessToken,
            name: profileName,
            slug: slug,
            settingsJson: {displayName: profileName, bio: '', profileImage: '', socials: {}, backgroundColor: '#ffffff'}
        });

        if (result.ok && result.data?.profile) {
            const created = result.data.profile;
            const newLocal = {
                id: created.id,
                name: created.name,
                slug: created.slug,
                createdAt: created.createdAt,
                settings: created.settingsJson || {displayName: profileName, bio: '', profileImage: '', socials: {}, backgroundColor: '#ffffff'},
                editorContent: null,
                _raw: created
            };

            setProfiles(prev => [...prev, newLocal]);
            setActiveProfileId(created.id);
            localStorage.setItem('blocknote-designer-active-profile', String(created.id));
            setSettings(newLocal.settings);
            setInitialContent(null);
            setCurrentEditorContent(null);
            setEditorKey(prev => prev + 1);
            setNewProfileName('');
            setIsNewProfile(false);
        } else {
            console.error('Failed to create profile:', result.error);
        }

        setTimeout(() => setIsTransitioning(false), 100);
    }, [newProfileName, profiles, activeProfileId, settings, currentEditorContent, tokens?.accessToken, isNewProfile]);

    // Rename profile via API
    const handleRenameProfile = useCallback(async () => {
        const newName = renameProfileName.trim();
        if (!newName || !renameProfileId) {return;}

        const newSlug = generateSlug(newName);
        const result = await updateProfileApi({
            accessToken: tokens?.accessToken,
            id: renameProfileId,
            name: newName,
            slug: newSlug
        });

        if (result.ok) {
            setProfiles(prev => prev.map(p => {
                if (String(p.id) === String(renameProfileId)) {
                    return {...p, name: newName, slug: newSlug, updatedAt: new Date().toISOString()};
                }
                return p;
            }));
        } else {
            console.error('Failed to rename profile:', result.error);
        }

        setShowRenameModal(false);
        setRenameProfileId(null);
        setRenameProfileName('');
    }, [renameProfileName, renameProfileId, tokens?.accessToken]);

    // Open rename modal
    const openRenameModal = useCallback((profile, e) => {
        e.stopPropagation();
        setRenameProfileId(profile.id);
        setRenameProfileName(profile.name);
        setShowRenameModal(true);
        setShowProfileMenu(false);
    }, []);

    // Switch profile - save current via API, then load target
    const handleSwitchProfile = useCallback(async (profileId) => {
        if (String(profileId) === String(activeProfileId)) {
            setShowProfileMenu(false);
            return;
        }

        setIsTransitioning(true);
        setShowProfileMenu(false);

        // Save current profile edits via API
        let editorContent = currentEditorContent;
        if (editorRef.current) {
            editorContent = editorRef.current.document;
        }

        if (activeProfileId && !isNewProfile) {
            await updateProfileApi({
                accessToken: tokens?.accessToken,
                id: activeProfileId,
                settingsJson: {...settings},
                editorState: {document: editorContent}
            });
            // Update local state
            setProfiles(prev => prev.map(p => {
                if (String(p.id) === String(activeProfileId)) {
                    return {...p, settings: {...settings}, editorContent, updatedAt: new Date().toISOString()};
                }
                return p;
            }));
        }

        const targetProfile = profiles.find(p => String(p.id) === String(profileId));
        localStorage.setItem('blocknote-designer-active-profile', String(profileId));

        setTimeout(() => {
            setActiveProfileId(profileId);
            if (targetProfile) {
                const loadedSettings = targetProfile.settings || {};
                setSettings({
                    displayName: '',
                    bio: '',
                    profileImage: '',
                    socials: {},
                    ...loadedSettings,
                    pageName: loadedSettings.pageName || targetProfile.name || ''
                });
                setInitialContent(targetProfile.editorContent || null);
                setCurrentEditorContent(targetProfile.editorContent || null);
            }
            setEditorKey(prev => prev + 1);

            setTimeout(() => setIsTransitioning(false), 100);
        }, 150);
    }, [profiles, activeProfileId, settings, currentEditorContent, tokens?.accessToken, isNewProfile]);

    // Delete profile via API
    const handleDeleteProfile = useCallback(async (profileId, e) => {
        e.stopPropagation();
        if (profiles.length <= 1) {
            return;
        }
        if (!confirm('Are you sure you want to delete this profile?')) {
            return;
        }

        const {deleteProfile: deleteProfileApi} = await import('../../utils/designerApi');
        const result = await deleteProfileApi({accessToken: tokens?.accessToken, id: profileId});
        if (!result.ok) {
            console.error('Failed to delete profile:', result.error);
            return;
        }

        const updatedProfiles = profiles.filter(p => String(p.id) !== String(profileId));

        if (String(activeProfileId) === String(profileId)) {
            setIsTransitioning(true);
            setShowProfileMenu(false);

            const newActiveProfile = updatedProfiles[0];
            localStorage.setItem('blocknote-designer-active-profile', String(newActiveProfile.id));

            setTimeout(() => {
                setProfiles(updatedProfiles);
                setActiveProfileId(newActiveProfile.id);
                const loadedSettings = newActiveProfile.settings || {};
                setSettings({
                    displayName: '',
                    bio: '',
                    profileImage: '',
                    socials: {},
                    ...loadedSettings,
                    pageName: loadedSettings.pageName || newActiveProfile.name || ''
                });
                setInitialContent(newActiveProfile.editorContent || null);
                setCurrentEditorContent(newActiveProfile.editorContent || null);
                setEditorKey(prev => prev + 1);
                setTimeout(() => setIsTransitioning(false), 100);
            }, 150);
        } else {
            setProfiles(updatedProfiles);
            setShowProfileMenu(false);
        }
    }, [profiles, activeProfileId, tokens?.accessToken]);

    const isDesktop = viewMode === 'desktop';

    // Template sheet drag handlers
    const handleTemplateSheetDragStart = useCallback((e) => {
        e.preventDefault();
        isDraggingRef.current = true;
        startYRef.current = e.clientY || e.touches?.[0]?.clientY;
        startHeightRef.current = templateSheetHeight;
        document.addEventListener('mousemove', handleTemplateSheetDrag);
        document.addEventListener('mouseup', handleTemplateSheetDragEnd);
        document.addEventListener('touchmove', handleTemplateSheetDrag);
        document.addEventListener('touchend', handleTemplateSheetDragEnd);
    }, [templateSheetHeight]);

    const handleTemplateSheetDrag = useCallback((e) => {
        if (!isDraggingRef.current) {
            return;
        }
        const clientY = e.clientY || e.touches?.[0]?.clientY;
        const deltaY = startYRef.current - clientY;
        const newHeight = Math.min(Math.max(startHeightRef.current + deltaY, 200), window.innerHeight - 100);
        setTemplateSheetHeight(newHeight);
    }, []);

    const handleTemplateSheetDragEnd = useCallback(() => {
        isDraggingRef.current = false;
        document.removeEventListener('mousemove', handleTemplateSheetDrag);
        document.removeEventListener('mouseup', handleTemplateSheetDragEnd);
        document.removeEventListener('touchmove', handleTemplateSheetDrag);
        document.removeEventListener('touchend', handleTemplateSheetDragEnd);
    }, [handleTemplateSheetDrag]);

    const handleTemplateClick = useCallback((templateId) => {
        const templateBlocks = BLOCK_TEMPLATES[templateId];
        const template = PROFILE_TEMPLATES.find(t => t.id === templateId);
        if (!templateBlocks || !editorRef.current) {
            console.log('Template not found or editor not ready:', templateId);
            setShowTemplates(false);
            return;
        }

        const editor = editorRef.current;

        // Remove all existing blocks
        const existingBlocks = editor.document;
        existingBlocks.forEach(block => {
            editor.removeBlocks([block]);
        });

        // Insert template blocks
        templateBlocks.forEach((blockConfig, index) => {
            const newBlock = {
                type: blockConfig.type,
                props: blockConfig.props || {},
                content: blockConfig.content || undefined
            };

            if (index === 0) {
                // For first block, insert at the beginning
                editor.insertBlocks([newBlock], editor.document[0], 'before');
            } else {
                // Insert after the last block
                const lastBlock = editor.document[editor.document.length - 1];
                editor.insertBlocks([newBlock], lastBlock, 'after');
            }
        });

        // Apply template's theme color as background
        if (template?.themeColor) {
            setSettings(prev => ({...prev, backgroundColor: template.themeColor}));
        }

        // Update current content state
        setCurrentEditorContent(editor.document);
        setShowTemplates(false);
    }, []);

    const handleTemplateGridScroll = useCallback((e) => {
        const scrollTop = e.target.scrollTop;
        const maxHeight = window.innerHeight - 80;
        const minHeight = 400;

        if (scrollTop > 0) {
            const newHeight = Math.min(minHeight + scrollTop * 1.5, maxHeight);
            setTemplateSheetHeight(newHeight);
        }
    }, []);

    return (
        <div ref={contentAreaRef} style={styles.container}>
            <div style={styles.editorWrapper}>
                {/* Breadcrumb */}
                <div style={styles.breadcrumb}>
                    <span
                        style={styles.breadcrumbLink}
                        onClick={() => {
                            // Clear the 'new' flag when going back
                            if (isNewProfile) {
                                localStorage.removeItem('blocknote-designer-active-profile');
                            }
                            navigate('/my-pages');
                        }}
                    >
                        My Pages
                    </span>
                    <span style={styles.breadcrumbSeparator}>&rsaquo;</span>
                    <span
                        style={styles.breadcrumbProfileName}
                        onClick={() => {
                            setRenameProfileId(currentProfile?.id);
                            setRenameProfileName(currentProfile?.name || '');
                            setShowRenameModal(true);
                        }}
                    >
                        {currentProfile?.name || 'Untitled'}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginLeft: '6px', verticalAlign: 'middle'}}>
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                    </span>
                </div>

                {/* Action Buttons */}
                <div style={styles.simpleHeader}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <button onClick={handleSave} style={styles.saveBtn} disabled={isSaving}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                                <polyline points="17 21 17 13 7 13 7 21"/>
                                <polyline points="7 3 7 8 15 8"/>
                            </svg>
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                        </button>
                        {/* Publish button - only visible after saving (profile has an ID and no unsaved changes) */}
                        {activeProfileId && !isNewProfile && !hasUnsavedChanges && (
                            <button
                                onClick={handlePublish}
                                disabled={isPublishing || currentProfile?._raw?.status === 'pending_review'}
                                style={{
                                    ...styles.saveBtn,
                                    opacity: (isPublishing || currentProfile?._raw?.status === 'pending_review') ? 0.6 : 1,
                                    cursor: (isPublishing || currentProfile?._raw?.status === 'pending_review') ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13"/>
                                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                                </svg>
                                <span>{isPublishing ? 'Submitting...' : currentProfile?._raw?.status === 'pending_review' ? 'Pending Review' : 'Publish'}</span>
                            </button>
                        )}
                    </div>
                </div>

                <div style={styles.mainContent}>
                    {showSettingsPanel && (
                        <>
                            {/* Backdrop to close settings when clicking outside */}
                            <div
                                onClick={() => setShowSettingsPanel(false)}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'transparent',
                                    zIndex: 49,
                                    cursor: 'default'
                                }}
                            />
                            <div style={styles.settingsPanel} onClick={(e) => e.stopPropagation()}>
                            <div style={styles.settingsPanelHeader}>
                                <h3 style={styles.settingsPanelTitle}>Settings</h3>
                                <button onClick={() => setShowSettingsPanel(false)} style={styles.closePanelBtn}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>

                            {/* Page Name Section */}
                            <div style={styles.settingsSection}>
                                <label style={styles.settingsLabel}>Page Name</label>
                                <input
                                    type="text"
                                    value={settings.pageName || settings.displayName || ''}
                                    onChange={(e) => updateSettings('pageName', e.target.value)}
                                    placeholder="Enter page name..."
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                <p style={{
                                    fontSize: '12px',
                                    color: '#91918e',
                                    margin: '6px 0 0 0'
                                }}>
                                    This name will be used to identify your page
                                </p>
                            </div>

                            <div style={styles.settingsSection}>
                                <label style={styles.settingsLabel}>Background Color</label>
                                <div style={styles.colorGrid}>
                                    {[
                                        {id: 'white', color: '#ffffff', label: 'White'},
                                        {id: 'light-gray', color: '#f5f5f5', label: 'Light Gray'},
                                        {id: 'pink', color: '#f8e8e8', label: 'Pink'},
                                        {id: 'beige', color: '#f5f0e8', label: 'Beige'},
                                        {id: 'mint', color: '#e8f5f0', label: 'Mint'},
                                        {id: 'teal', color: '#4a9c8c', label: 'Teal'},
                                        {id: 'navy', color: '#1a1a2e', label: 'Navy'},
                                        {id: 'charcoal', color: '#2d3436', label: 'Charcoal'}
                                    ].map(preset => (
                                        <button
                                            key={preset.id}
                                            onClick={() => updateSettings('backgroundColor', preset.color)}
                                            style={{
                                                ...styles.colorSwatch,
                                                backgroundColor: preset.color,
                                                border: settings.backgroundColor === preset.color
                                                    ? '3px solid #007AFF'
                                                    : '2px solid #e0e0e0',
                                                boxShadow: settings.backgroundColor === preset.color
                                                    ? '0 0 0 2px rgba(0,122,255,0.3)'
                                                    : 'none'
                                            }}
                                            title={preset.label}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div style={styles.settingsSection}>
                                <label style={styles.settingsLabel}>Social Links</label>
                                {SOCIAL_PLATFORMS.map(platform => (
                                    <div key={platform.id} style={styles.socialInputRow}>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#666">
                                            <path d={platform.icon}/>
                                        </svg>
                                        <input
                                            type="url"
                                            value={settings.socials?.[platform.id] || ''}
                                            onChange={(e) => updateSocial(platform.id, e.target.value)}
                                            placeholder={`${platform.name} URL`}
                                            style={styles.socialInput}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                        </>
                    )}

                    <div style={{
                        ...styles.editorArea,
                        ...(isDesktop ? styles.desktopEditorArea : styles.mobileEditorArea)
                    }}>
                        <div style={{
                            ...styles.layoutContainer,
                            ...(isDesktop ? styles.desktopLayoutContainer : styles.mobileLayoutContainer),
                            opacity: isTransitioning ? 0.5 : 1,
                            transition: 'opacity 0.15s ease-in-out'
                        }}>
                            {/* Editor Section */}
                            <div style={{
                                ...styles.editorSection,
                                ...(isDesktop ? styles.desktopEditorSection : styles.mobileEditorSection)
                            }}>
                                <div style={{
                                    ...styles.editorContainer,
                                    backgroundColor: settings.backgroundColor || '#ffffff'
                                }}>
                                    <BlockNoteEditorWrapper
                                        key={editorKey}
                                        initialContent={initialContent}
                                        onContentChange={handleEditorContentChange}
                                        editorRef={editorRef}
                                        onInsertBlock={(fn) => { insertBlockRef.current = fn; }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Toolbar */}
            <div style={{
                ...styles.bottomToolbar,
                left: `calc(50% + ${sidebarOffset / 2}px)` // Adjust center based on sidebar width
            }}>
                <button onClick={handleShare} style={styles.shareBtn}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3"/>
                        <circle cx="6" cy="12" r="3"/>
                        <circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                    </svg>
                    Share
                </button>

                <div style={styles.divider} />

                <button
                    onClick={() => setShowTemplates(!showTemplates)}
                    style={{
                        ...styles.templatesBtn,
                        backgroundColor: showTemplates ? '#f0f0f0' : 'transparent'
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" rx="1"/>
                        <rect x="14" y="3" width="7" height="7" rx="1"/>
                        <rect x="3" y="14" width="7" height="7" rx="1"/>
                        <rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    Templates
                </button>

                <div style={styles.divider} />

                <button
                    onClick={() => setViewMode('desktop')}
                    style={{
                        ...styles.viewBtn,
                        backgroundColor: viewMode === 'desktop' ? '#f0f0f0' : 'transparent'
                    }}
                    title="Desktop View"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={viewMode === 'desktop' ? '#000' : 'currentColor'} strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                        <line x1="8" y1="21" x2="16" y2="21"/>
                        <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                </button>

                <button
                    onClick={() => setViewMode('mobile')}
                    style={{
                        ...styles.viewBtn,
                        backgroundColor: viewMode === 'mobile' ? '#f0f0f0' : 'transparent'
                    }}
                    title="Mobile View"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={viewMode === 'mobile' ? '#000' : 'currentColor'} strokeWidth="2">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12.01" y2="18"/>
                    </svg>
                </button>

                <div style={styles.divider} />

                {/* Background Color Picker */}
                <div style={{position: 'relative'}} ref={colorPickerRef}>
                    <button
                        onClick={() => setShowColorPicker(!showColorPicker)}
                        style={{
                            ...styles.viewBtn,
                            backgroundColor: showColorPicker ? '#f0f0f0' : 'transparent',
                            position: 'relative'
                        }}
                        title="Background Color"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <circle cx="12" cy="12" r="6" fill={settings.backgroundColor || '#ffffff'} stroke="none"/>
                        </svg>
                        <div style={{
                            position: 'absolute',
                            bottom: '2px',
                            right: '2px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: settings.backgroundColor || '#ffffff',
                            border: '1px solid #ccc'
                        }} />
                    </button>

                    {/* Color Picker Popover */}
                    {showColorPicker && (
                        <>
                            <div
                                style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998}}
                                onClick={() => setShowColorPicker(false)}
                            />
                            <div style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '8px',
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                padding: '12px',
                                zIndex: 999,
                                minWidth: '200px'
                            }}>
                                <div style={{fontSize: '12px', fontWeight: 600, color: '#666', marginBottom: '8px'}}>
                                    Background Color
                                </div>
                                <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px'}}>
                                    {[
                                        {id: 'white', color: '#ffffff', label: 'White'},
                                        {id: 'light-gray', color: '#f5f5f5', label: 'Light Gray'},
                                        {id: 'pink', color: '#f8e8e8', label: 'Pink'},
                                        {id: 'beige', color: '#f5f0e8', label: 'Beige'},
                                        {id: 'mint', color: '#e8f5f0', label: 'Mint'},
                                        {id: 'teal', color: '#4a9c8c', label: 'Teal'},
                                        {id: 'navy', color: '#1a1a2e', label: 'Navy'},
                                        {id: 'charcoal', color: '#2d3436', label: 'Charcoal'}
                                    ].map(preset => (
                                        <button
                                            key={preset.id}
                                            onClick={() => {
                                                updateSettings('backgroundColor', preset.color);
                                                setShowColorPicker(false);
                                            }}
                                            style={{
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '8px',
                                                backgroundColor: preset.color,
                                                border: settings.backgroundColor === preset.color
                                                    ? '3px solid #007AFF'
                                                    : '2px solid #e0e0e0',
                                                cursor: 'pointer',
                                                padding: 0
                                            }}
                                            title={preset.label}
                                        />
                                    ))}
                                </div>
                                {/* Custom color input */}
                                <div style={{marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <input
                                        type="color"
                                        value={settings.backgroundColor || '#ffffff'}
                                        onChange={(e) => updateSettings('backgroundColor', e.target.value)}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '8px',
                                            border: '2px solid #e0e0e0',
                                            cursor: 'pointer',
                                            padding: 0
                                        }}
                                        title="Custom color"
                                    />
                                    <span style={{fontSize: '12px', color: '#666'}}>Custom</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Share Profile Modal */}
            {showShareModal && (
                <div style={styles.modalOverlay} onClick={() => setShowShareModal(false)}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Share Your Profile</h3>
                        <p style={styles.modalDescription}>
                            Anyone with this link can view your profile
                        </p>
                        <div style={styles.shareUrlContainer}>
                            <input
                                type="text"
                                value={shareUrl}
                                readOnly
                                style={styles.shareUrlInput}
                                onClick={(e) => e.target.select()}
                            />
                            <button
                                onClick={handleCopyShareUrl}
                                style={styles.copyBtn}
                            >
                                {shareCopied ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                                        <polyline points="20,6 9,17 4,12"/>
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        {shareCopied && (
                            <p style={styles.copiedMessage}>Link copied to clipboard!</p>
                        )}
                        <div style={styles.modalActions}>
                            <button onClick={() => setShowShareModal(false)} style={styles.modalCancelBtn}>
                                Close
                            </button>
                            <button
                                onClick={() => window.open(shareUrl, '_blank')}
                                style={styles.modalCreateBtn}
                            >
                                Open Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Save Success Modal */}
            {showSaveSuccessModal && (
                <div style={styles.modalOverlay} onClick={() => setShowSaveSuccessModal(false)}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <div style={{textAlign: 'center', marginBottom: '16px'}}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="9,12 12,15 16,9"/>
                            </svg>
                        </div>
                        <h3 style={{...styles.modalTitle, textAlign: 'center'}}>Profile Saved!</h3>
                        <p style={{...styles.modalDescription, textAlign: 'center'}}>
                            Your profile has been saved successfully.
                        </p>
                        <div style={{...styles.modalActions, justifyContent: 'center'}}>
                            <button
                                onClick={() => setShowSaveSuccessModal(false)}
                                style={styles.modalCreateBtn}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Profile Modal */}
            {showRenameModal && (
                <div style={styles.modalOverlay} onClick={() => setShowRenameModal(false)}>
                    <div style={styles.modal} onClick={e => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>Rename Page</h3>
                        <p style={styles.modalDescription}>
                            Enter a new name for this page
                        </p>
                        <input
                            type="text"
                            value={renameProfileName}
                            onChange={(e) => setRenameProfileName(e.target.value)}
                            placeholder="Page name"
                            style={styles.modalInput}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && renameProfileName.trim()) {
                                    handleRenameProfile();
                                }
                            }}
                        />
                        <div style={styles.modalActions}>
                            <button
                                onClick={() => {
                                    setShowRenameModal(false);
                                    setRenameProfileId(null);
                                    setRenameProfileName('');
                                }}
                                style={styles.modalCancelBtn}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRenameProfile}
                                style={styles.modalCreateBtn}
                                disabled={!renameProfileName.trim()}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Templates Bottom Sheet */}
            {showTemplates && (
                <>
                    <div
                        style={styles.templateSheetOverlay}
                        onClick={() => setShowTemplates(false)}
                    />
                    <div
                        ref={templateSheetRef}
                        style={{
                            ...styles.templateSheet,
                            height: `${templateSheetHeight}px`
                        }}
                    >
                        <div
                            style={styles.templateSheetHandle}
                            onMouseDown={handleTemplateSheetDragStart}
                            onTouchStart={handleTemplateSheetDragStart}
                        >
                            <div style={styles.templateSheetHandleBar} />
                        </div>

                        <div style={styles.templateSheetHeader}>
                            <h3 style={styles.templateSheetTitle}>Templates</h3>
                            <button
                                onClick={() => setShowTemplates(false)}
                                style={styles.templateSheetClose}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>

                        <div style={styles.templateGrid} onScroll={handleTemplateGridScroll}>
                            {PROFILE_TEMPLATES.map(template => (
                                <button
                                    key={template.id}
                                    onClick={() => handleTemplateClick(template.id)}
                                    style={styles.templateItem}
                                    className="template-item"
                                >
                                    <div style={{
                                        ...styles.templatePreview,
                                        background: template.background
                                    }}>
                                        {/* Header Row - Avatar and Social Icons */}
                                        {template.preview.avatarCompact ? (
                                            <div style={styles.templateCompactHeader}>
                                                {template.preview.avatarImage ? (
                                                    <img src={template.preview.avatarImage} alt="" style={styles.templateAvatarSmallImg} />
                                                ) : (
                                                    <div style={styles.templateAvatarSmall} />
                                                )}
                                                <div style={styles.templateCompactName}>{template.preview.title}</div>
                                                {template.preview.socialIcons && (
                                                    <div style={styles.templateSocialIcons}>
                                                        {template.preview.socialIcons.slice(0, 2).map((icon, i) => (
                                                            <div key={i} style={styles.templateSocialIcon} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : template.preview.brandCircle ? (
                                            <div style={styles.templateBrandCircle}>
                                                {template.preview.brandCircle.charAt(0)}
                                            </div>
                                        ) : (
                                            <>
                                                {template.preview.avatarImage ? (
                                                    <img src={template.preview.avatarImage} alt="" style={styles.templateAvatarImg} />
                                                ) : (
                                                    <div style={styles.templateAvatar} />
                                                )}
                                                <div style={styles.templatePreviewTitle}>
                                                    {template.preview.title}
                                                </div>
                                            </>
                                        )}

                                        {/* Live Status Badge */}
                                        {template.preview.liveStatus && (
                                            <div style={styles.templateLiveBadge}>LIVE</div>
                                        )}

                                        {/* Section Title */}
                                        {template.preview.sectionTitle && (
                                            <div style={styles.templateSectionTitle}>{template.preview.sectionTitle}</div>
                                        )}

                                        {/* Product Cards Row */}
                                        {template.preview.productCards && (
                                            <div style={styles.templateProductRow}>
                                                {template.preview.productCards.slice(0, 2).map((product, i) => (
                                                    <div key={i} style={styles.templateProductCard}>
                                                        {product.image ? (
                                                            <img src={product.image} alt="" style={styles.templateProductImageImg} />
                                                        ) : (
                                                            <div style={styles.templateProductImage} />
                                                        )}
                                                        <div style={styles.templateProductPrice}>{product.price}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Shoppable Post */}
                                        {template.preview.shoppablePost && (
                                            <div style={styles.templateShoppablePost}>
                                                {template.preview.shoppableImage ? (
                                                    <img src={template.preview.shoppableImage} alt="" style={styles.templateShoppableImageImg} />
                                                ) : (
                                                    <div style={styles.templateShoppableImage} />
                                                )}
                                            </div>
                                        )}

                                        {/* Video Section */}
                                        {(template.preview.videoSection || template.preview.videoEmbed) && (
                                            <div style={styles.templateVideoSection}>
                                                {template.preview.videoThumbnail && (
                                                    <img src={template.preview.videoThumbnail} alt="" style={styles.templateVideoThumbnailImg} />
                                                )}
                                                <div style={styles.templateVideoPlayBtn}>▶</div>
                                            </div>
                                        )}

                                        {/* Checklist Items */}
                                        {template.preview.checklistItems && (
                                            <div style={styles.templateChecklist}>
                                                {template.preview.checklistItems.slice(0, 2).map((item, i) => (
                                                    <div key={i} style={styles.templateChecklistItem}>✓ {item}</div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Buttons */}
                                        {template.preview.buttons && (
                                            <div style={styles.templatePreviewButtons}>
                                                {template.preview.buttons.slice(0, 2).map((btn, i) => (
                                                    <div key={i} style={styles.templatePreviewButton}>{btn}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div style={styles.templateInfo}>
                                        <span style={styles.templateName}>{template.name}</span>
                                        <span style={styles.templateSubtitle}>{template.subtitle}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Styling */}
            <style>{`
                .bn-container,
                .bn-editor,
                .bn-block-group,
                .bn-block-outer,
                .bn-block,
                [data-content-type],
                .mantine-Paper-root {
                    background: transparent !important;
                    background-color: transparent !important;
                }
                .bn-container {
                    border: none !important;
                    box-shadow: none !important;
                }
                .profile-edit-btn {
                    cursor: pointer !important;
                    pointer-events: auto !important;
                }
                .profile-edit-btn:hover {
                    background: #e0e0e0 !important;
                    border-color: #ccc !important;
                    color: #333 !important;
                }
                .profile-delete-btn {
                    cursor: pointer !important;
                    pointer-events: auto !important;
                }
                .profile-delete-btn:hover {
                    background: #ffe0e0 !important;
                    border-color: #ffb0b0 !important;
                    color: #c62828 !important;
                }
                .new-profile-btn {
                    cursor: pointer !important;
                    pointer-events: auto !important;
                }
                .new-profile-btn:hover {
                    background: #333 !important;
                }
                .profile-item:hover {
                    background-color: #f9f9f9;
                }
                .profile-item-actions button {
                    cursor: pointer !important;
                    pointer-events: auto !important;
                    position: relative;
                    z-index: 5;
                }
                .template-item:hover {
                    border-color: #000;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .template-item:active {
                    transform: translateY(0);
                    box-shadow: none;
                }
                .block-menu-item:hover {
                    background-color: #f5f5f5;
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { transform: translateX(-50%) translateY(100%); }
                    to { transform: translateX(-50%) translateY(0); }
                }
            `}</style>
        </div>
    );
}

// BlockNote Designer Preview component
export function BlockNoteDesignerPreview() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const editor = useCreateBlockNote({
        initialContent: profile?.editorContent || undefined
    });

    useEffect(() => {
        const activeProfileId = localStorage.getItem('blocknote-designer-active-profile');
        const savedProfiles = localStorage.getItem('blocknote-designer-profiles');

        if (savedProfiles) {
            try {
                const profiles = JSON.parse(savedProfiles);
                const foundProfile = profiles.find(p => p.id === activeProfileId) || profiles[0];
                setProfile(foundProfile);
            } catch (e) {
                console.error('Failed to parse profiles:', e);
            }
        }
        setIsLoading(false);
    }, []);

    if (isLoading) {
        return (
            <div style={previewStyles.container}>
                <div style={previewStyles.loading}>Loading...</div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div style={previewStyles.container}>
                <button onClick={() => navigate('/blocknote-designer')} style={previewStyles.backBtn}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Back to Editor
                </button>
                <div style={previewStyles.emptyState}>
                    <p>No profile found. Create one in the editor.</p>
                </div>
            </div>
        );
    }

    const settings = profile.settings || {};

    return (
        <CartProvider>
            <div style={previewStyles.container}>
                <button onClick={() => navigate('/blocknote-designer')} style={previewStyles.backBtn}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Back to Editor
                </button>

                <div style={previewStyles.profile}>
                    {settings.profileImage ? (
                        <div style={previewStyles.avatar}>
                            <img src={settings.profileImage} alt="" style={previewStyles.avatarImg} />
                        </div>
                    ) : (
                        <div style={previewStyles.avatarEmpty}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="#9ca3af">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                        </div>
                    )}

                    <h1 style={previewStyles.name}>{settings.displayName || 'Your Name'}</h1>

                    {settings.bio && (
                        <p style={previewStyles.bio}>{settings.bio}</p>
                    )}

                    {settings.socials && Object.values(settings.socials).some(v => v) && (
                        <div style={previewStyles.socials}>
                            {SOCIAL_PLATFORMS.filter(p => settings.socials?.[p.id]).map(platform => (
                                <a
                                    key={platform.id}
                                    href={settings.socials[platform.id]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={previewStyles.socialLink}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24">
                                        <path fill="currentColor" d={platform.icon}/>
                                    </svg>
                                </a>
                            ))}
                        </div>
                    )}

                    {profile.editorContent && (
                        <div style={previewStyles.content}>
                            <BlockNoteView
                                editor={editor}
                                editable={false}
                                theme="light"
                            />
                        </div>
                    )}
                </div>

                {/* Floating Cart Bar for brand products */}
                <FloatingCheckoutBar />
            </div>
        </CartProvider>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#fbfaf8',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        position: 'relative',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box'
    },
    editorWrapper: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
    },
    profileSelectorBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 24px',
        backgroundColor: 'transparent',
        borderBottom: '1px solid #e5e5e5',
        position: 'sticky',
        top: 0,
        zIndex: 100
    },
    simpleHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '12px 24px',
        backgroundColor: 'transparent',
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#666',
        padding: '12px 24px 4px 24px'
    },
    breadcrumbLink: {
        color: '#666',
        cursor: 'pointer',
        textDecoration: 'none'
    },
    breadcrumbSeparator: {
        color: '#ccc',
        fontSize: '16px'
    },
    breadcrumbProfileName: {
        color: '#1a1a1a',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '6px',
        transition: 'background-color 0.15s ease'
    },
    backBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        borderRadius: '8px',
        color: '#666'
    },
    profileSelectorWrapper: {
        position: 'relative'
    },
    profileSelector: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        color: '#1a1a1a'
    },
    profileSelectorLabel: {
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    blockNoteBadge: {
        backgroundColor: '#3b82f6',
        color: '#fff',
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: '4px',
        textTransform: 'uppercase'
    },
    profileDropdown: {
        position: 'absolute',
        top: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '300px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        zIndex: 200
    },
    profileDropdownHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid #e5e5e5',
        backgroundColor: '#fafafa',
        position: 'relative',
        zIndex: 10
    },
    profileDropdownTitle: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#666',
        letterSpacing: '0.5px'
    },
    newProfileBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '8px 14px',
        backgroundColor: '#000',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        position: 'relative',
        zIndex: 10,
        transition: 'background-color 0.15s ease',
        pointerEvents: 'auto'
    },
    profileList: {
        maxHeight: '280px',
        overflowY: 'auto'
    },
    profileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        transition: 'background-color 0.15s'
    },
    profileItemActive: {
        backgroundColor: '#f5f5f5'
    },
    profileItemInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: 1,
        minWidth: 0
    },
    profileItemName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    profileItemDate: {
        fontSize: '12px',
        color: '#9ca3af'
    },
    profileItemActions: {
        display: 'flex',
        gap: '4px',
        marginLeft: '8px',
        position: 'relative',
        zIndex: 10
    },
    profileEditBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        background: '#f0f0f0',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        color: '#666',
        cursor: 'pointer',
        transition: 'all 0.15s ease'
    },
    profileDeleteBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        background: '#fff0f0',
        border: '1px solid #ffd0d0',
        borderRadius: '6px',
        color: '#e53935',
        cursor: 'pointer',
        transition: 'all 0.15s ease'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    headerBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        cursor: 'pointer',
        color: '#666'
    },
    headerBtnActive: {
        background: '#000',
        color: '#fff',
        borderColor: '#000'
    },
    previewBtn: {
        padding: '8px 16px',
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer'
    },
    saveBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        color: '#37352f',
        cursor: 'pointer',
        transition: '0.15s'
    },
    mainContent: {
        flex: 1,
        display: 'flex',
        position: 'relative'
    },
    settingsPanel: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '320px',
        height: '100%',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e5e5e5',
        zIndex: 50,
        overflowY: 'auto',
        boxShadow: '-4px 0 20px rgba(0,0,0,0.05)'
    },
    settingsPanelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e5e5'
    },
    settingsPanelTitle: {
        fontSize: '16px',
        fontWeight: 600,
        margin: 0,
        color: '#1a1a1a'
    },
    closePanelBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        background: 'transparent',
        border: 'none',
        borderRadius: '6px',
        color: '#666',
        cursor: 'pointer'
    },
    settingsSection: {
        padding: '20px'
    },
    settingsLabel: {
        display: 'block',
        fontSize: '13px',
        fontWeight: 500,
        color: '#666',
        marginBottom: '12px'
    },
    colorGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '10px'
    },
    colorSwatch: {
        width: '100%',
        aspectRatio: '1',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
    },
    socialInputRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '10px'
    },
    socialInput: {
        flex: 1,
        padding: '10px 12px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none'
    },
    editorArea: {
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        overflowY: 'auto'
    },
    mobileEditorArea: {
        justifyContent: 'center',
        padding: '20px 20px 120px 20px'
    },
    desktopEditorArea: {
        justifyContent: 'flex-start',
        padding: '20px 20px 120px 80px'
    },
    layoutContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        maxWidth: '1100px',
        transition: 'max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1), gap 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    mobileLayoutContainer: {
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: '100%',
        gap: '0px'
    },
    desktopLayoutContainer: {
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: '60px',
        marginTop: '20px',
        marginLeft: 'auto'
    },
    editorSection: {
        transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1), flex 0.5s cubic-bezier(0.4, 0, 0.2, 1), margin 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease'
    },
    mobileEditorSection: {
        width: '100%',
        flex: '0 0 100%',
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'center'
    },
    desktopEditorSection: {
        flex: '1 1 auto',
        minWidth: 0,
        width: 'auto'
    },
    editorContainer: {
        background: 'transparent',
        width: '100%',
        maxWidth: '600px',
        borderRadius: '12px',
        // border: '1px solid #e5e5e5',
        padding: '20px',
        minHeight: '300px'
    },
    bottomToolbar: {
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        zIndex: 50
    },
    settingsBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        backgroundColor: 'transparent',
        color: '#333',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease'
    },
    shareBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#000',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer'
    },
    divider: {
        width: '1px',
        height: '24px',
        backgroundColor: '#e5e5e5'
    },
    viewBtn: {
        padding: '8px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    toolbarBtn: {
        padding: '8px',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
    },
    templatesBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        color: '#666',
        transition: 'background-color 0.15s ease'
    },
    templateSheetOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.3)',
        zIndex: 200,
        animation: 'fadeIn 0.25s ease-out'
    },
    templateSheet: {
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: '660px',
        backgroundColor: '#fff',
        borderRadius: '24px 24px 0 0',
        boxShadow: '0 -4px 30px rgba(0,0,0,0.15)',
        zIndex: 250,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideUp 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
        transition: 'height 0.15s ease-out'
    },
    templateSheetHandle: {
        display: 'flex',
        justifyContent: 'center',
        padding: '12px 0 8px 0',
        cursor: 'grab',
        touchAction: 'none'
    },
    templateSheetHandleBar: {
        width: '40px',
        height: '4px',
        backgroundColor: '#d1d5db',
        borderRadius: '2px'
    },
    templateSheetHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 24px 16px 24px',
        borderBottom: '1px solid #e5e5e5'
    },
    templateSheetTitle: {
        fontSize: '18px',
        fontWeight: 600,
        margin: 0,
        color: '#1a1a1a'
    },
    templateSheetClose: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        background: '#f5f5f5',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        color: '#666'
    },
    templateGrid: {
        flex: 1,
        overflowY: 'auto',
        padding: '16px 20px 100px 20px',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '14px',
        alignContent: 'start'
    },
    templateItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: 0,
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        overflow: 'hidden'
    },
    templatePreview: {
        padding: '16px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        minHeight: '140px'
    },
    templateBrandCircle: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 700,
        color: '#333'
    },
    templateAvatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.3)',
        border: '2px solid rgba(255,255,255,0.5)'
    },
    templatePreviewTitle: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#fff',
        textAlign: 'center',
        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
    },
    templatePreviewButtons: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
        marginTop: '4px'
    },
    templatePreviewButton: {
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: '6px 8px',
        borderRadius: '6px',
        fontSize: '9px',
        fontWeight: 500,
        color: '#333',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    templateCompactHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        padding: '0 4px'
    },
    templateAvatarSmall: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.3)',
        border: '2px solid rgba(255,255,255,0.5)',
        flexShrink: 0
    },
    templateAvatarSmallImg: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '2px solid rgba(255,255,255,0.5)',
        flexShrink: 0
    },
    templateAvatarImg: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '2px solid rgba(255,255,255,0.5)'
    },
    templateCompactName: {
        fontSize: '10px',
        fontWeight: 600,
        color: '#fff',
        textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        flex: 1,
        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    templateSocialIcons: {
        display: 'flex',
        gap: '4px'
    },
    templateSocialIcon: {
        width: '16px',
        height: '16px',
        borderRadius: '4px',
        backgroundColor: 'rgba(255,255,255,0.3)'
    },
    templateLiveBadge: {
        backgroundColor: '#ff4444',
        color: '#fff',
        fontSize: '8px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        alignSelf: 'flex-start'
    },
    templateSectionTitle: {
        fontSize: '9px',
        fontWeight: 600,
        color: 'rgba(255,255,255,0.9)',
        textAlign: 'left',
        width: '100%',
        marginTop: '4px'
    },
    templateProductRow: {
        display: 'flex',
        gap: '6px',
        width: '100%'
    },
    templateProductCard: {
        flex: 1,
        backgroundColor: 'rgba(255,255,255,0.15)',
        borderRadius: '6px',
        padding: '6px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px'
    },
    templateProductImage: {
        width: '100%',
        height: '28px',
        backgroundColor: 'rgba(255,255,255,0.2)',
        borderRadius: '4px'
    },
    templateProductImageImg: {
        width: '100%',
        height: '28px',
        objectFit: 'cover',
        borderRadius: '4px'
    },
    templateProductPrice: {
        fontSize: '8px',
        fontWeight: 600,
        color: '#fff'
    },
    templateShoppablePost: {
        width: '100%',
        height: '45px',
        backgroundColor: 'rgba(255,255,255,0.15)',
        borderRadius: '6px',
        overflow: 'hidden'
    },
    templateShoppableImage: {
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255,255,255,0.1)'
    },
    templateShoppableImageImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    templateVideoSection: {
        width: '100%',
        height: '40px',
        backgroundColor: 'rgba(0,0,0,0.3)',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
    },
    templateVideoThumbnailImg: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0.7
    },
    templateVideoPlayBtn: {
        width: '20px',
        height: '20px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '8px',
        color: '#333',
        position: 'relative',
        zIndex: 1
    },
    templateChecklist: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        width: '100%'
    },
    templateChecklistItem: {
        fontSize: '8px',
        color: 'rgba(255,255,255,0.9)',
        textAlign: 'left',
        padding: '2px 4px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: '3px'
    },
    templateInfo: {
        padding: '12px',
        borderTop: '1px solid #f0f0f0',
        backgroundColor: '#fafafa',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px'
    },
    templateName: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#1a1a1a',
        textAlign: 'left'
    },
    templateSubtitle: {
        fontSize: '11px',
        fontWeight: 400,
        color: '#888',
        textAlign: 'left'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
    },
    modalTitle: {
        fontSize: '18px',
        fontWeight: 600,
        margin: '0 0 8px 0',
        color: '#1a1a1a'
    },
    modalDescription: {
        fontSize: '14px',
        color: '#666',
        margin: '0 0 16px 0'
    },
    modalInput: {
        width: '100%',
        padding: '12px 16px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        marginBottom: '20px',
        boxSizing: 'border-box'
    },
    modalActions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
    },
    modalCancelBtn: {
        padding: '10px 20px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer'
    },
    modalCreateBtn: {
        padding: '10px 20px',
        backgroundColor: '#000',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer'
    },
    shareUrlContainer: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px'
    },
    shareUrlInput: {
        flex: 1,
        padding: '12px 16px',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '13px',
        outline: 'none',
        backgroundColor: '#f9f9f9',
        color: '#666',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    copyBtn: {
        padding: '12px 16px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s'
    },
    copiedMessage: {
        fontSize: '13px',
        color: '#22c55e',
        margin: '0 0 16px 0',
        textAlign: 'center'
    },
    menuOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50
    },
    // Blocks Menu styles
    addBlockBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        color: '#666',
        transition: 'background-color 0.15s ease'
    },
    blocksMenu: {
        position: 'absolute',
        bottom: '100%',
        left: 0,
        marginBottom: '8px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        width: '280px',
        maxHeight: '400px',
        overflowY: 'auto',
        zIndex: 300
    },
    blocksMenuHeader: {
        padding: '12px 16px',
        borderBottom: '1px solid #e5e5e5',
        fontSize: '11px',
        fontWeight: 600,
        color: '#666',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        backgroundColor: '#fafafa',
        borderRadius: '12px 12px 0 0'
    },
    blockMenuItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        width: '100%',
        padding: '12px 16px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color 0.15s ease',
        borderBottom: '1px solid #f0f0f0'
    },
    blockMenuIcon: {
        fontSize: '20px',
        lineHeight: 1,
        marginTop: '2px'
    },
    blockMenuTitle: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a',
        marginBottom: '2px'
    },
    blockMenuDesc: {
        fontSize: '12px',
        color: '#888'
    }
};

const previewStyles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '40px 20px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    },
    backBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        backgroundColor: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        maxWidth: '480px',
        margin: '0 auto 40px auto'
    },
    loading: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#666',
        fontSize: '16px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#666'
    },
    profile: {
        maxWidth: '480px',
        margin: '0 auto',
        textAlign: 'center'
    },
    avatar: {
        width: '96px',
        height: '96px',
        borderRadius: '50%',
        overflow: 'hidden',
        margin: '0 auto 24px auto',
        border: '3px solid #e5e5e5'
    },
    avatarEmpty: {
        width: '96px',
        height: '96px',
        borderRadius: '50%',
        margin: '0 auto 24px auto',
        border: '3px solid #e5e5e5',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    avatarImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    name: {
        fontSize: '28px',
        fontWeight: 700,
        margin: '0 0 8px 0',
        color: '#1a1a1a'
    },
    bio: {
        fontSize: '15px',
        color: '#666',
        margin: '0 0 20px 0',
        lineHeight: 1.5
    },
    socials: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '32px'
    },
    socialLink: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#f0f0f0',
        color: '#666',
        transition: 'background-color 0.2s'
    },
    content: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'left',
        border: '1px solid #e5e5e5'
    }
};
